import React, { useCallback, useEffect, useState } from 'react';
import { useGuardedCallback } from '../../permissions/useGuardedAction';
import {
  PermissionLevel,
  PermissionResource,
} from '../../permissions/permission-catalog';
import type { ApprovalItem } from '../../client';
import { ChatInteraction } from '../../hooks/useTeamChatSocket';
import { ApprovalRequestView } from '../../approvals/ApprovalRequestView';
import { WorkspaceApprovalCard } from '../../workspaces/WorkspaceApprovalCard';
import { InviteApprovalCard } from '../../approvals/InviteApprovalCard';
import { markdownToSafeHtml } from '../../lib/markdown';
import { useUserContext } from '../../state/UserContext';
import { useApi } from '../../state/ApiContext';
import { useApprovalEvents } from '../../state/ApprovalEventsContext';
import AsyncButton from '../AsyncButton';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { sessionFetch } from '../../session-client';

type PendingPermissionAction = 'grant' | 'decline' | null;

const InlineApprovalRequest: React.FC<{
  approvalId: number;
  interactionStatus?: 'open' | 'granted' | 'declined';
  actualName?: string;
  resolvedBy?: string;
  decisionNote?: string;
}> = ({
  approvalId,
  interactionStatus,
  actualName,
  resolvedBy,
  decisionNote,
}) => {
  const { approvalsApi } = useApi();
  const { epoch, invalidate } = useApprovalEvents();
  const [approval, setApproval] = useState<ApprovalItem | null>(null);
  const [deciding, setDeciding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const response = await approvalsApi.apiApprovalsApprovalIdGet(approvalId);
    const next = response.data.approval as ApprovalItem;
    setApproval({
      ...next,
      status:
        interactionStatus === 'granted'
          ? 'approved'
          : interactionStatus === 'declined'
            ? 'rejected'
            : next.status,
      detail: {
        ...next.detail,
        ...(actualName ? { actualName } : {}),
        ...(resolvedBy ? { resolvedBy } : {}),
      },
      decisionNote: decisionNote || next.decisionNote,
    });
  }, [
    actualName,
    approvalId,
    approvalsApi,
    decisionNote,
    interactionStatus,
    resolvedBy,
  ]);

  useEffect(() => {
    load().catch(() => setError('Could not load this approval request.'));
  }, [load, epoch]);

  // Declared BEFORE the early return below: a hook after a conditional return
  // is only called on some renders, which React reports as "Rendered more hooks
  // than during the previous render" the moment `approval` arrives.
  const decideGuard = useGuardedCallback<
    [
      decision: 'approved' | 'rejected',
      note: string,
      inputs?: Record<string, unknown>,
    ],
    Promise<void>
  >({
    resource: ['secret_create', 'secret_access', 'secret_mutation'].includes(
      approval?.type || '',
    )
      ? PermissionResource.TEAM_SECRET
      : PermissionResource.APPROVAL,
    level: ['secret_create', 'secret_access', 'secret_mutation'].includes(
      approval?.type || '',
    )
      ? PermissionLevel.EDIT
      : PermissionLevel.MANAGE,
    verb: 'decide',
    subject: 'pending approvals',
    testId: 'chat-approval-decide-denied',
    onAllowed: async (decision, note, inputs) => {
      setDeciding(true);
      setError(null);
      try {
        await approvalsApi.apiApprovalsApprovalIdDecisionPost(approvalId, {
          decision,
          note: note || undefined,
          inputs,
        });
        invalidate(approvalId);
        await load();
      } catch {
        setError('Could not save the decision. It may already be resolved.');
      } finally {
        setDeciding(false);
      }
    },
  });

  if (!approval) {
    return <div role="status">{error || 'Loading approval request…'}</div>;
  }

  return (
    <>
      <ApprovalRequestView
        approval={approval}
        deciding={deciding}
        headerless
        onDecide={(decision, note, inputs) =>
          // The card awaits this to hold its own spinner, so forward the real
          // promise; a refusal has no request to wait for.
          decideGuard.run(decision, note, inputs) ?? Promise.resolve()
        }
      />
      {decideGuard.denialModal}
    </>
  );
};

const MONITOR_STATUS_BADGES: Record<
  Exclude<Extract<ChatInteraction, { kind: 'monitor' }>['status'], 'pending'>,
  string
> = {
  done: '✓ Monitor finished',
  timeout: '⏱ Monitor timed out',
  canceled: '✕ Monitor canceled',
  error: '⚠ Monitor failed',
};

/**
 * A background monitor's inline card. While pending it shows the check
 * cadence + deadline with a Cancel button (the agent stays paused either
 * way — cancel wakes it with a "canceled by user" notice). Terminal states
 * render as a static badge; the status flip arrives via the server-side
 * interaction update on reload/refetch.
 */
const MonitorCard: React.FC<{
  interaction: Extract<ChatInteraction, { kind: 'monitor' }>;
  teamId?: string;
}> = ({ interaction, teamId }) => {
  const [localStatus, setLocalStatus] = useState<'canceled' | null>(null);
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = localStatus ?? interaction.status;

  if (status !== 'pending') {
    return (
      <div
        className={`chat-interaction chat-interaction-monitor chat-interaction-monitor-${status}`}
        data-testid="interaction-monitor">
        <span className="chat-interaction-monitor-badge">
          {MONITOR_STATUS_BADGES[status]}
        </span>
      </div>
    );
  }

  // deadlineAt is a UTC ISO string; render it in the viewer's own timezone
  // (no explicit timeZone → the browser's local zone) and show the zone
  // abbreviation so it reads unambiguously as their local time.
  const deadline = new Date(interaction.deadlineAt);
  const deadlineLabel = deadline.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  const cancel = async () => {
    setCanceling(true);
    setError(null);
    try {
      const res = await sessionFetch(
        `/api/teams/${teamId}/workspace-monitors/${interaction.monitorUuid}/cancel`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(
          body?.error || `Could not cancel the monitor (${res.status}).`,
        );
      }
      setLocalStatus('canceled');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCanceling(false);
    }
  };

  const percent =
    typeof interaction.progressPercent === 'number' &&
    Number.isFinite(interaction.progressPercent)
      ? Math.min(100, Math.max(0, interaction.progressPercent))
      : null;

  return (
    <div
      className="chat-interaction chat-interaction-monitor chat-interaction-monitor-pending"
      data-testid="interaction-monitor">
      <div className="chat-interaction-monitor-row">
        <span className="chat-interaction-monitor-spinner" aria-hidden="true" />
        <span className="chat-interaction-monitor-text">
          Monitoring — checking every {interaction.checkIntervalSeconds}s until{' '}
          {deadlineLabel}
        </span>
      </div>
      {(interaction.progressMessage || percent !== null) && (
        <div
          className="chat-interaction-monitor-progress"
          data-testid="interaction-monitor-progress">
          {percent !== null && (
            <div
              className="chat-interaction-monitor-progressbar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}>
              <div
                className="chat-interaction-monitor-progressbar-fill"
                style={{ width: `${percent}%` }}
              />
            </div>
          )}
          <span className="chat-interaction-monitor-progress-label">
            {percent !== null ? `${percent}% — ` : ''}
            {interaction.progressMessage || ''}
          </span>
        </div>
      )}
      {teamId && (
        <AsyncButton
          type="button"
          className="chat-interaction-monitor-cancel"
          loading={canceling}
          loadingText="Canceling..."
          onClick={() => void cancel()}>
          Cancel
        </AsyncButton>
      )}
      {error && <div className="chat-interaction-error">{error}</div>}
    </div>
  );
};

const DEFERRED_WAIT_BADGES: Record<
  Exclude<
    Extract<ChatInteraction, { kind: 'deferred_wait' }>['status'],
    'pending'
  >,
  string
> = {
  completed: '✓ Finished',
  // Not phrased as a failure: nothing broke, the clock simply ran out, and
  // the agent has already been woken and told so.
  max_wait_reached: '⏱ Max time reached',
  failed: '⚠ Failed',
  canceled: '✕ Canceled',
};

/**
 * A deferred wait's inline card — the "agent is waiting" indicator.
 *
 * While pending it names what is being waited for and when the agent gives
 * up, with a Cancel button. Cancelling does not strand the agent: it is a
 * terminal outcome like any other and wakes it with a "canceled by user"
 * notice.
 */
const DeferredWaitCard: React.FC<{
  interaction: Extract<ChatInteraction, { kind: 'deferred_wait' }>;
  teamId?: string;
}> = ({ interaction, teamId }) => {
  const [localStatus, setLocalStatus] = useState<'canceled' | null>(null);
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = localStatus ?? interaction.status;

  if (status !== 'pending') {
    return (
      <div
        className={`chat-interaction chat-interaction-wait chat-interaction-wait-${status}`}
        data-testid="interaction-deferred-wait">
        <span className="chat-interaction-wait-badge">
          {DEFERRED_WAIT_BADGES[status]}
        </span>
        <span className="chat-interaction-wait-reason">
          {interaction.reason}
        </span>
      </div>
    );
  }

  // deadlineAt is a UTC ISO string; rendered in the viewer's own timezone
  // with the zone abbreviation so it reads unambiguously as their local time.
  const deadlineLabel = new Date(interaction.deadlineAt).toLocaleString(
    undefined,
    {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    },
  );

  const cancel = async () => {
    setCanceling(true);
    setError(null);
    try {
      const res = await sessionFetch(
        `/api/teams/${teamId}/deferred-waits/${interaction.waitUuid}/cancel`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(
          body?.error || `Could not cancel the wait (${res.status}).`,
        );
      }
      setLocalStatus('canceled');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCanceling(false);
    }
  };

  return (
    <div
      className="chat-interaction chat-interaction-wait chat-interaction-wait-pending"
      data-testid="interaction-deferred-wait">
      <div className="chat-interaction-wait-row">
        <span className="chat-interaction-wait-spinner" aria-hidden="true" />
        <span className="chat-interaction-wait-text">
          Waiting for {interaction.reason} — until {deadlineLabel}
        </span>
      </div>
      {interaction.progressMessage && (
        <div className="chat-interaction-wait-progress">
          {interaction.progressMessage}
        </div>
      )}
      {teamId && (
        <AsyncButton
          type="button"
          className="chat-interaction-wait-cancel"
          loading={canceling}
          loadingText="Canceling..."
          onClick={() => void cancel()}>
          Cancel
        </AsyncButton>
      )}
      {error && <div className="chat-interaction-error">{error}</div>}
    </div>
  );
};

interface InteractionPromptProps {
  interaction: ChatInteraction;
  /** The agent that posed the prompt — mentioned in the resume so it continues. */
  agentId?: number;
  agentName?: string;
  /** Send a reply into the conversation (re-triggers the asking agent). */
  onRespond: (text: string) => void;
  /**
   * Whether this is the newest interaction in the thread. Questions are
   * only answerable while newest (answering a stale question is
   * confusing), but an UNRESOLVED permission request stays actionable even
   * when later chatter buries it — otherwise a busy multi-agent card can
   * leave the user unable to grant the very thing the agents are blocked
   * on.
   */
  newest: boolean;
  teamId?: string;
  /**
   * Persist the grant/decline outcome on the message that carries this
   * prompt, so it renders resolved ("Approved by X") after a reload
   * instead of staying actionable forever. Absent on surfaces that can't
   * identify the message (the prompt then only resolves locally).
   */
  onResolve?: (status: 'granted' | 'declined') => void;
}

/**
 * Renders an agent's interactive prompt inline: a seek-feedback question
 * (option buttons + an "Other" free-text) or a capability request (Grant /
 * Decline, with a params modal on grant that saves directly). Used by both
 * the team chat and the project-card comment thread.
 */
export const InteractionPrompt: React.FC<InteractionPromptProps> = ({
  interaction,
  agentId,
  agentName,
  onRespond,
  newest,
  teamId,
  onResolve,
}) => {
  const [other, setOther] = useState('');
  const [done, setDone] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // The grant modal is a blocking overlay — lock the page behind it
  useBodyScrollLock(showModal);
  /** Grant/decline done in this session (persisted resolution arrives on reload) */
  const [localResolution, setLocalResolution] = useState<
    'granted' | 'declined' | null
  >(null);
  const { user } = useUserContext();
  // Config fields may arrive prefilled (e.g. the API hosts the agent
  // asked for) — the user reviews/edits them in the modal before granting
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (interaction.kind === 'permission' ? interaction.fields || [] : [])
        .filter((field) => field.value)
        .map((field) => [field.key, field.value!]),
    ),
  );
  const [pendingAction, setPendingAction] =
    useState<PendingPermissionAction>(null);
  const granting = pendingAction !== null;
  const [decisionNote, setDecisionNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { approvalsApi } = useApi();
  const { invalidate } = useApprovalEvents();

  const mention = agentName ? `@${agentName} ` : '';
  const resolved =
    interaction.kind === 'permission'
      ? interaction.status === 'granted' || interaction.status === 'declined'
      : interaction.kind === 'question'
        ? interaction.status === 'answered'
        : false;
  const disabled =
    done || resolved || (interaction.kind === 'question' && !newest);

  const respond = (text: string) => {
    if (done) return;
    setDone(true);
    onRespond(`${mention}${text}`.trim());
  };

  if (interaction.kind === 'question') {
    const options = interaction.options || [];
    return (
      <div className="chat-interaction" data-testid="interaction-question">
        {options.length > 0 && (
          <div className="chat-interaction-options">
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                className="chat-interaction-option"
                disabled={disabled}
                onClick={() => respond(opt)}>
                {opt}
              </button>
            ))}
          </div>
        )}
        {interaction.allowOther !== false && (
          <form
            className="chat-interaction-other"
            onSubmit={(e) => {
              e.preventDefault();
              if (other.trim()) respond(other.trim());
            }}>
            <input
              type="text"
              value={other}
              disabled={disabled}
              onChange={(e) => setOther(e.target.value)}
              placeholder={options.length > 0 ? 'Other…' : 'Type your answer…'}
            />
            <button type="submit" disabled={disabled || !other.trim()}>
              Send
            </button>
          </form>
        )}
      </div>
    );
  }

  if (interaction.kind === 'resource_request') {
    return teamId ? (
      <WorkspaceApprovalCard teamId={teamId} token={interaction.token} />
    ) : (
      <div className="workspace-resource-error" role="alert">
        Open this request from its team to review resources.
      </div>
    );
  }

  if (interaction.kind === 'monitor') {
    return <MonitorCard interaction={interaction} teamId={teamId} />;
  }

  if (interaction.kind === 'deferred_wait') {
    return <DeferredWaitCard interaction={interaction} teamId={teamId} />;
  }

  if (interaction.kind === 'invite_user') {
    return interaction.approvalId ? (
      <InviteApprovalCard approvalId={interaction.approvalId} />
    ) : (
      <div className="workspace-resource-error" role="alert">
        This invite request is not available yet — refresh the conversation.
      </div>
    );
  }

  if (interaction.kind === 'secret_create') {
    return interaction.approvalId ? (
      <InlineApprovalRequest
        approvalId={interaction.approvalId}
        interactionStatus={interaction.status}
        actualName={interaction.actualName}
        resolvedBy={interaction.resolvedBy}
        decisionNote={interaction.decisionNote}
      />
    ) : (
      <div className="workspace-resource-error" role="alert">
        This secret request is not available yet — refresh the conversation.
      </div>
    );
  }

  /*
   * Permission / capability request — the last variant standing. It is
   * narrowed rather than reached by elimination because the union also
   * carries 'form_submitted', which is a transcript notice rather than a
   * prompt (it renders as its own 'notice' item upstream and asks the user
   * for nothing). An early return for it here would sit ABOVE the guard
   * hooks below, which is a rules-of-hooks violation the guard-hook-order
   * audit catches; narrowing keeps every hook unconditional.
   */
  const permission = interaction.kind === 'permission' ? interaction : null;
  const fields = permission?.fields || [];

  /**
   * Granting a capability takes ONE of two paths and they are different
   * resources, decided by whether an approval record exists:
   *
   *   with an approvalId -> POST /api/approvals/:id/decision, `approval: manage`
   *   without one        -> PUT  /api/agents/:id/tools/bundles/:key,
   *                         `agent: manage`
   *
   * So the guard is picked per path rather than one being assumed. Declining
   * writes nothing at all without an approvalId — it just posts a chat reply —
   * which is why the decline path only consults the approval guard when there
   * IS a record to decide.
   */
  const approvalGuard = useGuardedCallback<[action: () => void]>({
    resource: PermissionResource.APPROVAL,
    level: PermissionLevel.MANAGE,
    verb: 'decide',
    subject: 'pending approvals',
    testId: 'interaction-approval-denied',
    onAllowed: (action) => action(),
  });
  const capabilityGuard = useGuardedCallback<[action: () => void]>({
    resource: PermissionResource.AGENT,
    level: PermissionLevel.MANAGE,
    verb: 'grant',
    subject: 'capabilities to this agent',
    testId: 'interaction-capability-denied',
    onAllowed: (action) => action(),
  });
  const grant = async () => {
    setPendingAction('grant');
    setError(null);
    try {
      const credentials: Record<string, string> = {};
      const config: Record<string, string> = {};
      for (const field of fields) {
        const value = (fieldValues[field.key] || '').trim();
        if (!value) continue;
        if (field.secret) credentials[field.key] = value;
        else config[field.key] = value;
      }
      const missing = fields.filter(
        (f) => f.secret && !(fieldValues[f.key] || '').trim(),
      );
      if (missing.length > 0) {
        throw new Error(
          `Please provide: ${missing.map((f) => f.label).join(', ')}`,
        );
      }
      if (permission?.approvalId) {
        await approvalsApi.apiApprovalsApprovalIdDecisionPost(
          permission?.approvalId,
          {
            decision: 'approved',
            note: decisionNote.trim() || undefined,
            inputs: {
              credentials,
              config,
            },
          },
        );
        invalidate(permission?.approvalId);
      } else {
        const res = await sessionFetch(
          `/api/agents/${agentId}/tools/bundles/${permission?.bundleKey}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              enabled: true,
              credentials,
              config,
            }),
          },
        );
        if (!res.ok) {
          throw new Error(`Could not grant the capability (${res.status}).`);
        }
      }
      setDone(true);
      setShowModal(false);
      setLocalResolution('granted');
      if (!permission?.approvalId) {
        onResolve?.('granted');
        onRespond(
          `${mention}✅ I've granted you the "${permission?.title}" capability. Please go ahead.`,
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPendingAction(null);
    }
  };

  /**
   * What accepting does once the check has passed: open the field form if the
   * capability needs inputs, otherwise grant straight away. Checked BEFORE the
   * form opens, not after it is filled in — a form whose save will be refused
   * is worse than being told now.
   */
  const acceptIfAllowed = () => {
    if (fields.length > 0) setShowModal(true);
    else void grant();
  };

  const decline = async () => {
    if (done) return;
    setPendingAction('decline');
    setError(null);
    try {
      if (permission?.approvalId) {
        await approvalsApi.apiApprovalsApprovalIdDecisionPost(
          permission?.approvalId,
          {
            decision: 'rejected',
            note: decisionNote.trim() || undefined,
          },
        );
        invalidate(permission?.approvalId);
      } else {
        onResolve?.('declined');
        onRespond(
          `${mention}I can't grant "${permission?.title}" right now — please continue without it or suggest an alternative.`,
        );
      }
      setDone(true);
      setLocalResolution('declined');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPendingAction(null);
    }
  };

  // Persisted outcome (survives reloads) or the one from this session
  const resolution = localResolution
    ? {
        status: localResolution,
        by:
          `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
          user?.email ||
          'you',
      }
    : permission?.status === 'granted' || permission?.status === 'declined'
      ? {
          status: permission?.status,
          by: permission?.resolvedBy || 'a team member',
        }
      : null;

  /**
   * The card next-action pair is bookkeeping between the platform and the
   * agent, not a request aimed at anyone. The question is addressed to the
   * agent alone, so it gets no rendering at all; the answer gets a labelled,
   * inert summary. Both sit here rather than with the early returns above so
   * that no hook is skipped on their path.
   */
  if (interaction.kind === 'card_next_action_question') return null;

  if (interaction.kind === 'card_next_action_answer') {
    const recorded = interaction.actionIds?.length ?? 0;
    return (
      <div
        className="chat-interaction chat-interaction-next-action"
        data-testid="interaction-card-next-action">
        <div className="chat-interaction-next-action-head">🗒 Next actions</div>
        <div className="chat-interaction-next-action-body">
          {recorded > 0
            ? `${recorded} follow-up${recorded === 1 ? '' : 's'} recorded on this card.`
            : 'Nothing further on this card.'}
        </div>
      </div>
    );
  }

  /**
   * Default for anything this component has no renderer for. It must be
   * "show nothing", never the permission block below: that block is the
   * fall-through every unhandled kind used to reach, and with `permission`
   * null it rendered as "🔐 Requesting access:" with an empty subject and a
   * live Grant button — a phantom approval for a capability nobody asked
   * for. An unrecognised interaction is not a request for access, and
   * failing towards asking a human to grant one is the wrong direction.
   */
  if (!permission) return null;

  return (
    <div
      className={`chat-interaction chat-interaction-permission${
        resolution ? ' chat-interaction-permission-resolved' : ''
      }`}
      data-testid="interaction-permission">
      <div className="chat-interaction-permission-head">
        🔐 Requesting access: <strong>{permission?.title}</strong>
      </div>
      {permission?.description && (
        <div className="chat-interaction-permission-desc">
          {permission?.description}
        </div>
      )}
      {/* No-fields grants have no modal, so the how-to renders inline */}
      {permission?.instructions && fields.length === 0 && !resolution && (
        <div
          className="chat-interaction-instructions markdown-content"
          data-testid="interaction-instructions"
          dangerouslySetInnerHTML={{
            __html: markdownToSafeHtml(permission?.instructions),
          }}
        />
      )}
      {resolution ? (
        <div
          className={`chat-interaction-resolution ${
            resolution.status === 'granted' ? 'approved' : 'declined'
          }`}
          data-testid="interaction-resolution">
          {resolution.status === 'granted' ? '✓ Approved' : '✕ Denied'} by{' '}
          {resolution.by}
        </div>
      ) : (
        <>
          {fields.length === 0 && (
            <label className="chat-interaction-decision-note">
              <span>Note to agent (optional)</span>
              <textarea
                aria-label="Note to agent"
                maxLength={4000}
                value={decisionNote}
                disabled={granting}
                onChange={(event) => setDecisionNote(event.target.value)}
              />
            </label>
          )}
          <div className="chat-interaction-actions">
            <AsyncButton
              type="button"
              className="chat-interaction-grant"
              disabled={disabled || pendingAction === 'decline'}
              loading={pendingAction === 'grant'}
              loadingText="Granting..."
              onClick={() =>
                (permission?.approvalId ? approvalGuard : capabilityGuard).run(
                  acceptIfAllowed,
                )
              }>
              Grant access
            </AsyncButton>
            <AsyncButton
              type="button"
              className="chat-interaction-decline"
              disabled={disabled || pendingAction === 'grant'}
              loading={pendingAction === 'decline'}
              loadingText="Declining..."
              onClick={() =>
                permission?.approvalId
                  ? approvalGuard.run(() => void decline())
                  : void decline()
              }>
              Not now
            </AsyncButton>
          </div>
        </>
      )}

      {showModal && (
        <div
          className="interaction-modal-backdrop"
          onClick={() => !granting && setShowModal(false)}>
          <div
            className="interaction-modal"
            onClick={(e) => e.stopPropagation()}>
            <h3>Grant “{permission?.title}”</h3>
            <p className="interaction-modal-sub">
              {permission?.title} needs the following to work. Values are stored
              securely for this agent and never shown back to it.
            </p>
            {permission?.instructions && (
              <div
                className="chat-interaction-instructions markdown-content"
                data-testid="interaction-instructions"
                dangerouslySetInnerHTML={{
                  __html: markdownToSafeHtml(permission?.instructions),
                }}
              />
            )}
            {fields.map((field) => (
              <label key={field.key} className="interaction-modal-field">
                <span>
                  {field.label}
                  {field.secret ? ' (kept secret)' : ''}
                </span>
                <input
                  type={field.secret ? 'password' : 'text'}
                  placeholder={field.placeholder}
                  value={fieldValues[field.key] || ''}
                  onChange={(e) =>
                    setFieldValues((prev) => ({
                      ...prev,
                      [field.key]: e.target.value,
                    }))
                  }
                />
              </label>
            ))}
            {error && <div className="interaction-modal-error">{error}</div>}
            <label className="interaction-modal-field">
              <span>Note to agent (optional)</span>
              <textarea
                aria-label="Note to agent"
                maxLength={4000}
                value={decisionNote}
                onChange={(event) => setDecisionNote(event.target.value)}
              />
            </label>
            <div className="interaction-modal-actions">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                disabled={granting}>
                Cancel
              </button>
              <AsyncButton
                type="button"
                className="interaction-modal-save"
                onClick={() =>
                  (permission?.approvalId
                    ? approvalGuard
                    : capabilityGuard
                  ).run(() => void grant())
                }
                loading={pendingAction === 'grant'}
                loadingText="Saving...">
                Save & grant
              </AsyncButton>
            </div>
          </div>
        </div>
      )}
      {error && !showModal && (
        <div className="chat-interaction-error">{error}</div>
      )}
      {approvalGuard.denialModal}
      {capabilityGuard.denialModal}
    </div>
  );
};
